'use client';
import React from 'react';
import { gsap } from 'gsap';
import useMediaQuery from '@mui/material/useMediaQuery';
import { CanvasContainer } from './styles';

export interface Props {
  numberOfFrames?: number;
  desktopURL?: string;
  mobileURL?: string;
  mobileHeight?: number;
  desktopHeight?: number;
  ariaLabel?: string;
}

const currentFrame = (index: number, imgUrl?: string) => {
  return `${imgUrl}/${index + 1}.webp`;
};

const Canvas = ({ numberOfFrames = 0, desktopURL, mobileURL, ariaLabel }: Props) => {
  const canvas = React.useRef<HTMLCanvasElement | null>(null);
  const context = React.useRef<CanvasRenderingContext2D | null>(null);
  const frameScrubber = React.useRef<any>(null);
  const fadeOut = React.useRef<any>(null);
  const currentFrameIndex = React.useRef({ frame: 0 });
  const isPortrait = useMediaQuery((theme: any) => theme.breakpoints.down('md'));
  const width = isPortrait ? 1080 : 1920; // These have to be the size of the images
  const height = isPortrait ? 1920 : 1080;

  const render = () => {
    context.current?.clearRect(0, 0, width, height);
    context.current?.drawImage(images[currentFrameIndex.current.frame], 0, 0);
  };

  const buildImages = () => {
    if (canvas.current) {
      canvas.current.width = width;
      canvas.current.height = height;
      context.current = canvas.current.getContext('2d');
    }
    return [...Array(numberOfFrames)].map((_, i) => {
      const img = new Image();
      img.src = currentFrame(i, isPortrait ? mobileURL : desktopURL);
      return img;
    });
  };

  const [images, setImages] = React.useState<Array<HTMLImageElement>>(buildImages);

  React.useEffect(() => {
    setImages(buildImages);
  }, [isPortrait]);

  React.useEffect(() => {
    images[currentFrameIndex.current.frame].onload = render;
    render();
  }, [images, render, currentFrameIndex]);

  const getRefs = (canvasRef: HTMLCanvasElement) => {
    if (!canvas.current) {
      canvasRef.width = width;
      canvasRef.height = height;
      canvas.current = canvasRef;
      context.current = canvasRef.getContext('2d');
    }
  };

  React.useEffect(() => {
    if (frameScrubber.current) frameScrubber?.current?.scrollTrigger?.kill();
    if (fadeOut.current) fadeOut?.current?.scrollTrigger?.kill();

    frameScrubber.current = gsap.to(currentFrameIndex.current, {
      frame: numberOfFrames - 1,
      snap: 'frame',
      ease: 'none',
      scrollTrigger: {
        trigger: '.scroll-copy',
        start: `bottom 75%`,
        end: `bottom bottom`,
        endTrigger: '.scroll-section',
        scrub: 0.5,
      },
      onUpdate: render, // use animation onUpdate instead of scrollTrigger's onUpdate
    });

    fadeOut.current = gsap.to(`.scroll-section`, {
      opacity: 0,
      ease: 'none',
      scrollTrigger: {
        trigger: `.scroll-section`,
        scrub: 1,
        start: 'bottom bottom',
      },
    });

    return () => {
      frameScrubber?.current?.scrollTrigger?.kill();
      fadeOut?.current?.scrollTrigger?.kill();
    };
  }, [frameScrubber, fadeOut, images]);

  React.useEffect(() => {
    if (context) {
      images[0].onload = render;
    }
  }, [images, context]);

  return (
    <CanvasContainer>
      <canvas ref={getRefs} role="img" aria-label={ariaLabel} />
    </CanvasContainer>
  );
};

export default Canvas;
